import { useRef, useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { z } from "zod"

import { useAgentState } from "@/agent-state"
import { useAxiosClientContext } from "@/components/core/AxiosInstanceProvider/useAxiosClient"
import { useToast } from "@/hooks/toastHook"
import { Decoder } from "@/pages/CustomConnectPanel/value"

const refreshCaseResponseSchema = z.object({
  data: z.discriminatedUnion("updated", [
    z.object({ updated: z.literal(false), case_id: z.string() }),
    z.object({
      updated: z.literal(true),
      selected_channels: Decoder.selectedChannelsSchema,
      case_id: z.string(),
    }),
  ]),
})

/**
 * Mutation hook to refresh a case's Q&A data (selected_channels) from the
 * current student data and campaign filter.
 *
 * @param caseId - The case document ID to refresh.
 * @returns A TanStack Query mutation object.
 */
const COOLDOWN_MS = 5_000

export function useRefreshCaseChannels(caseId: string | undefined) {
  const axiosClient = useAxiosClientContext()
  const queryClient = useQueryClient()
  const toast = useToast()
  const { send } = useAgentState()
  const [coolingDown, setCoolingDown] = useState(false)
  const cooldownTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const mutation = useMutation({
    mutationFn: async () => {
      if (!caseId) {
        throw new Error("Case ID is required")
      }

      const response = await axiosClient.post(`/case/${caseId}/refresh`)

      return refreshCaseResponseSchema.parse(response.data)
    },
    onError: () => {
      toast.error("caseHistory.refreshError")
    },
    onSettled: () => {
      setCoolingDown(true)
      cooldownTimer.current = setTimeout(
        () => setCoolingDown(false),
        COOLDOWN_MS,
      )
    },
    onSuccess: (data) => {
      if (data.data.updated) {
        toast.success("caseHistory.refreshUpdated")

        send({
          type: "CASE_CHANNELS_REFRESHED",
          selectedChannels: data.data.selected_channels,
        })

        void queryClient.invalidateQueries({ queryKey: ["/case"] })
      } else {
        toast.success("caseHistory.refreshUpToDate")
      }
    },
  })

  return { ...mutation, isDisabled: mutation.isPending || coolingDown }
}
